import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Package, ShoppingBag, Users, TrendingUp, Plus, Pencil, Trash2, X, LogOut, Upload, Loader2, Star, Check, ShieldCheck } from "lucide-react";
import { api, RON, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["Comandă nouă", "Confirmată", "În pregătire", "Expediată", "Livrată", "Anulată", "Retur", "În procesare"];
const EMPTY = {
  name: "", brand: "Lavazza", type: "boabe", category: "Cafea boabe", price: "", sale_price: "",
  sale_start: "", sale_end: "", stock: 0, sku: "", grammage: "1 kg", composition: "Arabica & Robusta",
  origin: "", intensity: 5, intensity_scale: 13, roast: "Medie", aroma_notes: "", acidity: "Medie", brewing: "",
  compatibility: "", ingredients: "100% cafea prăjită", storage: "Loc uscat și răcoros",
  producer: "", description: "", images: [], featured: false, is_new: false, bestseller: false, active: true,
};

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!loading && !user) navigate("/autentificare");
  }, [loading, user, navigate]);

  if (loading || !user) return <div className="pt-40 pb-40 text-center text-muted-foreground">Se încarcă...</div>;
  if (user.role !== "admin")
    return (
      <div className="pt-40 pb-40 text-center px-5" data-testid="admin-denied">
        <h1 className="font-display text-5xl tracking-tight">Acces interzis</h1>
        <p className="text-muted-foreground mt-4">Acest panou este disponibil doar administratorilor.</p>
      </div>
    );

  return (
    <div className="pt-20 min-h-screen bg-cream">
      <section className="bg-ink text-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 flex items-center justify-between">
          <div>
            <p className="overline text-sand">Panou administrare</p>
            <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-2">Coffee Cup Supply</h1>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="flex items-center gap-2 text-cream/70 hover:text-sand text-sm">
            <LogOut size={16} /> Ieși
          </button>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <div className="flex gap-2 border-b border-line mb-10">
          {[["overview", "Sumar"], ["products", "Produse"], ["orders", "Comenzi"], ["coupons", "Cupoane"], ["reviews", "Recenzii"], ["brands", "Branduri"], ["faq", "FAQ"], ["fbt", "Frecvent împreună"], ["settings", "Setări"], ["newsletter", "Newsletter"]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`admin-tab-${v}`}
              className={`px-5 py-3 text-sm transition-colors border-b-2 -mb-px whitespace-nowrap ${tab === v ? "border-roast text-roast font-semibold" : "border-transparent text-ink/60 hover:text-ink"}`}>{l}</button>
          ))}
        </div>
        {tab === "overview" && <Overview />}
        {tab === "products" && <Products />}
        {tab === "orders" && <Orders />}
        {tab === "coupons" && <Coupons />}
        {tab === "reviews" && <Reviews />}
        {tab === "brands" && <Brands />}
        {tab === "faq" && <Faq />}
        {tab === "fbt" && <Fbt />}
        {tab === "settings" && <Settings />}
        {tab === "newsletter" && <Newsletter />}
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);
  if (!stats) return <p className="text-muted-foreground">Se încarcă...</p>;
  const cards = [
    { icon: TrendingUp, label: "Venit (plătit/ramburs)", value: RON(stats.revenue) },
    { icon: ShoppingBag, label: "Comenzi", value: stats.orders },
    { icon: Package, label: "Produse", value: stats.products },
    { icon: Users, label: "Clienți", value: stats.customers },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="admin-stats">
      {cards.map((c) => (
        <div key={c.label} className="border border-line bg-surface p-6">
          <c.icon size={22} className="text-roast" />
          <p className="font-display text-4xl mt-6">{c.value}</p>
          <p className="text-xs text-muted-foreground mt-1">{c.label}</p>
        </div>
      ))}
    </div>
  );
}

function Products() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/products").then(({ data }) => setProducts(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => {
    setForm({ ...EMPTY, ...p, sale_price: p.sale_price ?? "", sale_start: p.sale_start ?? "", sale_end: p.sale_end ?? "", images: p.images || [] });
    setEditing(p.slug);
  };

  const uploadImages = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const full = `${BACKEND}${data.url}`;
        setForm((f) => ({ ...f, images: [...f.images, full] }));
      }
      toast.success("Imagini încărcate");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Eroare la încărcare");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    if (form.images.length === 0) { toast.error("Adaugă cel puțin o imagine"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form, price: Number(form.price), intensity: Number(form.intensity), intensity_scale: Number(form.intensity_scale) || 13, stock: Number(form.stock),
        sale_price: form.sale_price === "" ? null : Number(form.sale_price),
        sale_start: form.sale_start || null, sale_end: form.sale_end || null,
      };
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing}`, payload);
      toast.success("Produs salvat");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (slug) => {
    if (!window.confirm("Sigur ștergi acest produs? Acțiunea este definitivă.")) return;
    await api.delete(`/admin/products/${slug}`);
    toast.message("Produs șters");
    load();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const chk = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-muted-foreground">{products.length} produse</p>
        <button onClick={openNew} data-testid="admin-add-product" className="flex items-center gap-2 bg-ink text-cream px-5 py-3 hover:bg-roast transition-colors text-sm font-semibold">
          <Plus size={16} /> Adaugă produs
        </button>
      </div>

      <div className="border border-line bg-surface divide-y divide-line">
        {products.map((p) => (
          <div key={p.slug} className="flex items-center gap-4 p-4" data-testid={`admin-product-${p.slug}`}>
            <img src={p.image} alt={p.name} className="h-14 w-12 object-cover bg-stone" />
            <div className="flex-1 min-w-0">
              <p className="overline text-roast">{p.brand} · {p.type}</p>
              <p className="font-medium truncate">{p.name}</p>
              <p className="text-xs text-muted-foreground">
                Stoc: {p.stock} · {p.stock_status === "out" ? "epuizat" : p.stock_status === "limited" ? "limitat" : "în stoc"}
                {!p.active && " · inactiv"} {p.on_sale && ` · −${p.discount_percent}%`}
              </p>
            </div>
            <span className="text-sm w-28 text-right">
              {p.on_sale && <span className="line-through text-muted-foreground mr-1 text-xs">{RON(p.old_price)}</span>}
              {RON(p.effective_price)}
            </span>
            <button onClick={() => openEdit(p)} className="p-2 hover:text-roast" data-testid={`admin-edit-${p.slug}`} aria-label="Editează"><Pencil size={16} /></button>
            <button onClick={() => del(p.slug)} className="p-2 hover:text-destructive" data-testid={`admin-delete-${p.slug}`} aria-label="Șterge"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {editing && (
          <>
            <motion.div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setEditing(null)} />
            <motion.div className="fixed top-0 right-0 z-50 h-full w-full max-w-xl bg-cream overflow-y-auto"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.4, ease: [0.16, 1, 0.3, 1] }} data-testid="admin-product-form">
              <div className="sticky top-0 bg-cream flex items-center justify-between px-6 h-20 border-b border-line z-10">
                <h2 className="font-display text-3xl">{editing === "new" ? "Produs nou" : "Editează produs"}</h2>
                <button onClick={() => setEditing(null)} aria-label="Închide"><X size={22} /></button>
              </div>
              <form onSubmit={save} className="p-6 space-y-5">
                {/* images */}
                <div>
                  <span className="overline text-ink/50">Imagini</span>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {form.images.map((img, i) => (
                      <div key={i} className="relative h-20 w-16 bg-stone">
                        <img src={img} alt="" className="h-full w-full object-cover" />
                        <button type="button" onClick={() => removeImage(i)} className="absolute -top-2 -right-2 h-6 w-6 grid place-items-center rounded-full bg-ink text-cream" aria-label="Șterge imagine"><X size={12} /></button>
                      </div>
                    ))}
                    <label className="h-20 w-16 border-2 border-dashed border-line grid place-items-center cursor-pointer hover:border-roast transition-colors" data-testid="pf-upload-label">
                      {uploading ? <Loader2 size={18} className="animate-spin text-roast" /> : <Upload size={18} className="text-muted-foreground" />}
                      <input type="file" accept="image/*" multiple hidden data-testid="pf-image-upload"
                        onChange={(e) => uploadImages(Array.from(e.target.files))} />
                    </label>
                  </div>
                </div>

                <Row><F label="Nume"><input required value={form.name} onChange={set("name")} data-testid="pf-name" className="inp" /></F>
                  <F label="Brand"><input required value={form.brand} onChange={set("brand")} data-testid="pf-brand" className="inp" /></F></Row>

                <Row>
                  <F label="Tip"><select value={form.type} onChange={set("type")} data-testid="pf-type" className="inp"><option value="boabe">Boabe</option><option value="măcinată">Măcinată</option><option value="capsule">Capsule</option><option value="paduri">Paduri</option></select></F>
                  <F label="Categorie"><input value={form.category} onChange={set("category")} data-testid="pf-category" className="inp" /></F>
                </Row>

                <Row><F label="Preț normal (lei)"><input required type="number" step="0.01" value={form.price} onChange={set("price")} data-testid="pf-price" className="inp" /></F>
                  <F label="Stoc"><input required type="number" min="0" value={form.stock} onChange={set("stock")} data-testid="pf-stock" className="inp" /></F></Row>

                <div className="border border-line p-4 space-y-4 bg-stone/40">
                  <p className="overline text-roast">Reducere (opțional)</p>
                  <Row><F label="Preț redus (lei)"><input type="number" step="0.01" value={form.sale_price} onChange={set("sale_price")} data-testid="pf-sale-price" className="inp" /></F>
                    <F label="Gramaj"><input value={form.grammage} onChange={set("grammage")} data-testid="pf-grammage" className="inp" /></F></Row>
                  <Row><F label="Reducere de la"><input type="date" value={form.sale_start} onChange={set("sale_start")} data-testid="pf-sale-start" className="inp" /></F>
                    <F label="Reducere până la"><input type="date" value={form.sale_end} onChange={set("sale_end")} data-testid="pf-sale-end" className="inp" /></F></Row>
                </div>

                <Row><F label="SKU"><input value={form.sku} onChange={set("sku")} data-testid="pf-sku" className="inp" placeholder="auto dacă e gol" /></F>
                  <F label="Intensitate (din cât)"><div className="flex items-center gap-2">
                    <input type="number" min="1" max={form.intensity_scale || 13} value={form.intensity} onChange={set("intensity")} data-testid="pf-intensity" className="inp" />
                    <span className="text-ink/50 text-sm">din</span>
                    <input type="number" min="1" max="20" value={form.intensity_scale} onChange={set("intensity_scale")} data-testid="pf-intensity-scale" className="inp" />
                  </div></F></Row>

                <Row><F label="Prăjire"><select value={form.roast} onChange={set("roast")} data-testid="pf-roast" className="inp"><option>Medie</option><option>Intensă</option></select></F>
                  <F label="Compoziție"><input value={form.composition} onChange={set("composition")} data-testid="pf-composition" className="inp" /></F></Row>

                <Row><F label="Origine"><input value={form.origin} onChange={set("origin")} data-testid="pf-origin" className="inp" /></F>
                  <F label="Aciditate"><input value={form.acidity} onChange={set("acidity")} data-testid="pf-acidity" className="inp" /></F></Row>

                <F label="Note aromatice"><input value={form.aroma_notes} onChange={set("aroma_notes")} data-testid="pf-aroma" className="inp" /></F>
                <F label="Moduri de preparare"><input value={form.brewing} onChange={set("brewing")} data-testid="pf-brewing" className="inp" /></F>
                <F label="Compatibilitate aparate"><input value={form.compatibility} onChange={set("compatibility")} data-testid="pf-compat" className="inp" /></F>
                <F label="Ingrediente"><input value={form.ingredients} onChange={set("ingredients")} data-testid="pf-ingredients" className="inp" /></F>
                <Row><F label="Păstrare"><input value={form.storage} onChange={set("storage")} data-testid="pf-storage" className="inp" /></F>
                  <F label="Producător"><input value={form.producer} onChange={set("producer")} data-testid="pf-producer" className="inp" /></F></Row>
                <F label="Descriere"><textarea rows={3} value={form.description} onChange={set("description")} data-testid="pf-description" className="inp resize-none" /></F>

                <div className="flex flex-wrap gap-5 pt-2">
                  <Toggle label="Recomandat" checked={form.featured} onChange={chk("featured")} testid="pf-featured" />
                  <Toggle label="Nou" checked={form.is_new} onChange={chk("is_new")} testid="pf-new" />
                  <Toggle label="Bestseller" checked={form.bestseller} onChange={chk("bestseller")} testid="pf-bestseller" />
                  <Toggle label="Activ (vizibil)" checked={form.active} onChange={chk("active")} testid="pf-active" />
                </div>

                <button type="submit" disabled={saving} data-testid="pf-submit" className="w-full bg-ink text-cream py-4 hover:bg-roast transition-colors text-sm font-semibold disabled:opacity-60">
                  {saving ? "Se salvează..." : "Salvează produsul"}
                </button>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState(null);
  const [search, setSearch] = useState("");
  const [notes, setNotes] = useState({});
  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success("Status actualizat · email trimis clientului");
    load();
  };
  const saveNote = async (id) => {
    await api.put(`/admin/orders/${id}/note`, { note: notes[id] || "" });
    toast.success("Notă salvată");
  };
  const exportCsv = async () => {
    const { data } = await api.get("/admin/orders/export", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "comenzi.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const resetOrders = async () => {
    const confirmText = window.prompt("ATENȚIE: Aceasta șterge TOATE comenzile și resetează seria de proformă (ex: pentru lansarea oficială). Această acțiune este ireversibilă.\n\nScrie RESET pentru a confirma:");
    if (confirmText !== "RESET") { toast.info("Resetare anulată"); return; }
    try {
      const { data } = await api.post("/admin/orders/reset");
      toast.success(`${data.deleted} comenzi șterse · seria resetată`);
      load();
    } catch (e) {
      toast.error("Resetarea a eșuat");
    }
  };
  const PROFORMA_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/orders`;

  if (!orders) return <p className="text-muted-foreground">Se încarcă...</p>;
  const filtered = orders.filter((o) => {
    const s = search.toLowerCase();
    return !s || o.order_number.toLowerCase().includes(s) || (o.full_name || "").toLowerCase().includes(s) ||
      (o.email || "").toLowerCase().includes(s) || (o.phone || "").includes(s);
  });

  return (
    <div className="space-y-4" data-testid="admin-orders">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută după număr, nume, email sau telefon..."
          data-testid="admin-order-search" className="inp max-w-md" />
        <div className="flex gap-3">
          <button onClick={exportCsv} data-testid="admin-export-orders" className="bg-ink text-cream px-5 py-3 text-sm font-semibold hover:bg-roast transition-colors">Export CSV</button>
          <button onClick={resetOrders} data-testid="admin-reset-orders" className="border border-red-300 text-red-600 px-5 py-3 text-sm font-semibold hover:bg-red-50 transition-colors">Resetare comenzi</button>
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="text-muted-foreground py-10" data-testid="admin-no-orders">Nicio comandă găsită.</p>
      ) : filtered.map((o) => (
        <div key={o.id} className="border border-line bg-surface p-5" data-testid={`admin-order-${o.order_number}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="overline text-roast">{o.proforma_no || o.order_number}</p>
              <p className="text-xs text-muted-foreground">{o.full_name} · {o.phone} · {o.email}</p>
              <p className="text-xs text-muted-foreground">{o.address}, {o.city}, {o.county} · {new Date(o.created_at).toLocaleString("ro-RO")}</p>
              <p className="text-xs text-muted-foreground">
                {o.payment_method} · {o.payment_status === "paid" ? "plătit" : o.payment_status} · {o.items.length} produse
                {o.company_name ? ` · Firmă: ${o.company_name} (CUI ${o.cui})` : ""}
                {o.discount_code ? ` · cod ${o.discount_code} (−${RON(o.discount || 0)})` : ""}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-display text-2xl">{RON(o.total)}</span>
              <select value={STATUSES.includes(o.status) ? o.status : "În procesare"} onChange={(e) => changeStatus(o.id, e.target.value)}
                data-testid={`admin-order-status-${o.order_number}`} className="border border-line bg-cream px-3 py-2 text-sm outline-none">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <input defaultValue={o.admin_note || ""} onChange={(e) => setNotes((n) => ({ ...n, [o.id]: e.target.value }))}
              placeholder="Notă internă..." data-testid={`admin-note-${o.order_number}`}
              className="flex-1 bg-cream border border-line px-3 py-2 text-sm outline-none focus:border-roast" />
            <button onClick={() => saveNote(o.id)} data-testid={`admin-note-save-${o.order_number}`} className="border border-line px-4 text-sm hover:border-ink">Salvează nota</button>
            <a href={`${PROFORMA_BASE}/${o.order_number}/proforma.pdf`} target="_blank" rel="noopener noreferrer"
              data-testid={`admin-proforma-${o.order_number}`} className="border border-line px-4 py-2 text-sm hover:border-ink flex items-center">Proformă PDF</a>
          </div>
        </div>
      ))}
    </div>
  );
}

function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", kind: "percent", value: "", min_order: 0, expires: "" });
  const load = () => api.get("/admin/coupons").then(({ data }) => setCoupons(data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", { ...form, value: Number(form.value), min_order: Number(form.min_order), expires: form.expires || null });
      toast.success("Cod creat");
      setForm({ code: "", kind: "percent", value: "", min_order: 0, expires: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const del = async (code) => { await api.delete(`/admin/coupons/${code}`); toast.message("Cod șters"); load(); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="admin-coupons">
      <form onSubmit={create} className="border border-line bg-surface p-6 grid sm:grid-cols-5 gap-4 items-end mb-8">
        <F label="Cod"><input required value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} data-testid="coupon-code" className="inp" /></F>
        <F label="Tip"><select value={form.kind} onChange={set("kind")} data-testid="coupon-kind" className="inp"><option value="percent">Procent %</option><option value="fixed">Sumă fixă (lei)</option></select></F>
        <F label="Valoare"><input required type="number" step="0.01" value={form.value} onChange={set("value")} data-testid="coupon-value" className="inp" /></F>
        <F label="Comandă min. (lei)"><input type="number" value={form.min_order} onChange={set("min_order")} data-testid="coupon-min" className="inp" /></F>
        <button type="submit" data-testid="coupon-create" className="bg-ink text-cream py-3 text-sm font-semibold hover:bg-roast transition-colors">Creează cod</button>
      </form>
      <div className="border border-line bg-surface divide-y divide-line">
        {coupons.length === 0 && <p className="p-5 text-muted-foreground text-sm">Niciun cod încă.</p>}
        {coupons.map((c) => (
          <div key={c.code} className="flex items-center gap-4 p-4" data-testid={`coupon-row-${c.code}`}>
            <span className="font-display text-2xl">{c.code}</span>
            <span className="text-sm text-muted-foreground flex-1">{c.kind === "percent" ? `${c.value}%` : RON(c.value)}{c.min_order ? ` · min ${RON(c.min_order)}` : ""}</span>
            <button onClick={() => del(c.code)} className="p-2 hover:text-destructive" data-testid={`coupon-del-${c.code}`} aria-label="Șterge"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => api.get(`/admin/reviews${filter ? `?status=${filter}` : ""}`).then(({ data }) => setReviews(data));
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => {
    await api.put(`/admin/reviews/${id}/status`, { status });
    toast.success(status === "approved" ? "Recenzie aprobată" : status === "rejected" ? "Recenzie respinsă" : "Status actualizat");
    load();
  };
  const del = async (id) => { await api.delete(`/admin/reviews/${id}`); toast.message("Recenzie ștearsă"); load(); };
  const [replyDraft, setReplyDraft] = useState({});
  const saveReply = async (id) => {
    const text = (replyDraft[id] ?? "").trim();
    if (!text) { toast.error("Scrie un răspuns"); return; }
    await api.put(`/admin/reviews/${id}/reply`, { reply: text });
    toast.success("Răspuns salvat");
    setReplyDraft((d) => { const n = { ...d }; delete n[id]; return n; });
    load();
  };
  const delReply = async (id) => { await api.delete(`/admin/reviews/${id}/reply`); toast.message("Răspuns șters"); load(); };

  return (
    <div data-testid="admin-reviews">
      <div className="flex gap-2 mb-6">
        {[["pending", "În așteptare"], ["approved", "Aprobate"], ["rejected", "Respinse"], ["", "Toate"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`review-filter-${v || "all"}`}
            className={`px-4 py-2 text-sm border transition-colors ${filter === v ? "bg-ink text-cream border-ink" : "border-line hover:border-ink"}`}>{l}</button>
        ))}
      </div>
      <div className="border border-line bg-surface divide-y divide-line">
        {reviews.length === 0 && <p className="p-5 text-muted-foreground text-sm">Nicio recenzie.</p>}
        {reviews.map((r) => (
          <div key={r.id} className="p-5" data-testid={`review-row-${r.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="flex text-roast">
                    {[...Array(5)].map((_, i) => <Star key={i} size={14} fill={i < r.rating ? "currentColor" : "none"} />)}
                  </span>
                  <span className="font-medium text-sm">{r.user_name}</span>
                  {r.verified_purchase && (
                    <span className="inline-flex items-center gap-1 text-xs text-roast bg-roast/10 px-2 py-0.5" data-testid={`review-verified-${r.id}`}>
                      <ShieldCheck size={12} /> Achiziție verificată
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">· {r.product_slug}</span>
                </div>
                {r.title && <p className="font-medium mt-2">{r.title}</p>}
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{r.comment}</p>
                <p className="text-xs text-ink/40 mt-2">{new Date(r.created_at).toLocaleDateString("ro-RO")} · status: {r.status}</p>
                {r.admin_reply && (
                  <div className="mt-3 border-l-2 border-roast bg-roast/5 pl-3 py-2" data-testid={`review-reply-${r.id}`}>
                    <p className="text-[11px] font-semibold text-roast uppercase tracking-wide">Răspuns Coffee Cup Supply</p>
                    <p className="text-sm text-ink/80 mt-1">{r.admin_reply}</p>
                  </div>
                )}
                <div className="mt-3">
                  <textarea rows={2} value={replyDraft[r.id] ?? (r.admin_reply || "")}
                    onChange={(e) => setReplyDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                    data-testid={`review-reply-input-${r.id}`} placeholder="Scrie un răspuns public..."
                    className="w-full border border-line bg-white px-3 py-2 text-sm outline-none focus:border-roast resize-none" />
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => saveReply(r.id)} data-testid={`review-reply-save-${r.id}`}
                      className="border border-line px-3 py-1.5 text-xs hover:border-ink transition-colors">{r.admin_reply ? "Actualizează răspunsul" : "Trimite răspuns"}</button>
                    {r.admin_reply && (
                      <button onClick={() => delReply(r.id)} data-testid={`review-reply-del-${r.id}`}
                        className="px-3 py-1.5 text-xs text-destructive hover:underline">Șterge răspunsul</button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {r.status !== "approved" && (
                  <button onClick={() => setStatus(r.id, "approved")} data-testid={`review-approve-${r.id}`}
                    className="inline-flex items-center gap-1 bg-ink text-cream px-3 py-2 text-xs font-semibold hover:bg-roast transition-colors"><Check size={14} /> Aprobă</button>
                )}
                {r.status !== "rejected" && (
                  <button onClick={() => setStatus(r.id, "rejected")} data-testid={`review-reject-${r.id}`}
                    className="border border-line px-3 py-2 text-xs hover:border-ink transition-colors">Respinge</button>
                )}
                <button onClick={() => del(r.id)} data-testid={`review-del-${r.id}`} className="p-2 hover:text-destructive" aria-label="Șterge"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const mdPreview = (text) => {
  if (!text) return "";
  const esc = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" style="color:#8B3A2B">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
};

function Newsletter() {
  const [subs, setSubs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ subject: "", subtitle: "", content: "", image_url: "", cta_label: "", cta_url: "" });
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/admin/newsletter/subscribers").then(({ data }) => { setSubs(data); setSelected(data.map((s) => s.email)); });
    api.get("/admin/newsletter/campaigns").then(({ data }) => setCampaigns(data));
  };
  useEffect(() => { load(); }, []);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleSub = (email) => setSelected((s) => s.includes(email) ? s.filter((e) => e !== email) : [...s, email]);
  const allSelected = subs.length > 0 && selected.length === subs.length;

  const applyFormat = (kind) => {
    const el = document.querySelector("[data-testid='nl-content']");
    if (!el) return;
    const start = el.selectionStart, end = el.selectionEnd;
    const val = form.content;
    const sel = val.slice(start, end);
    let wrapped, caret;
    if (kind === "bold") { wrapped = `**${sel || "text"}**`; caret = start + 2; }
    else if (kind === "italic") { wrapped = `*${sel || "text"}*`; caret = start + 1; }
    else { wrapped = `[${sel || "text"}](https://coffeecup-supply.com)`; caret = start + 1; }
    const next = val.slice(0, start) + wrapped + val.slice(end);
    setForm((f) => ({ ...f, content: next }));
    requestAnimationFrame(() => {
      el.focus();
      if (sel) el.setSelectionRange(start, start + wrapped.length);
      else el.setSelectionRange(caret, caret + 4);
    });
  };

  const send = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.content.trim()) return toast.error("Completează subiectul și conținutul");
    if (selected.length === 0) return toast.error("Selectează cel puțin un abonat");
    if (!window.confirm(`Trimiți newsletterul către ${selected.length} abonați?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", { ...form, recipients: selected });
      toast.success(`Newsletter trimis · ${data.sent}/${data.recipients} livrate`);
      setForm({ subject: "", subtitle: "", content: "", image_url: "", cta_label: "", cta_url: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-10" data-testid="admin-newsletter">
      <div>
        <form onSubmit={send} className="space-y-4 border border-line bg-surface p-6">
          <p className="font-display text-2xl">Compune newsletter</p>
          <Row>
            <F label="Subtitlu (mic, roșu)"><input value={form.subtitle} onChange={setF("subtitle")} data-testid="nl-subtitle" className="inp" placeholder="Ex: Ofertă limitată" /></F>
            <F label="Imagine sus (URL banner)"><input value={form.image_url} onChange={setF("image_url")} data-testid="nl-image" className="inp" placeholder="https://..." /></F>
          </Row>
          <F label="Titlu mare"><input value={form.subject} onChange={setF("subject")} data-testid="nl-subject" className="inp" placeholder="Ex: Reduceri de toamnă la Lavazza" /></F>
          <F label="Conținut">
            <div className="flex items-center gap-1.5 mb-2">
              <button type="button" onClick={() => applyFormat("bold")} data-testid="nl-fmt-bold"
                className="h-8 w-8 grid place-items-center border border-line bg-white text-sm font-bold hover:bg-surface transition-colors" title="Îngroșat (**text**)">B</button>
              <button type="button" onClick={() => applyFormat("italic")} data-testid="nl-fmt-italic"
                className="h-8 w-8 grid place-items-center border border-line bg-white text-sm italic hover:bg-surface transition-colors" title="Cursiv (*text*)">I</button>
              <button type="button" onClick={() => applyFormat("link")} data-testid="nl-fmt-link"
                className="h-8 px-2.5 grid place-items-center border border-line bg-white text-xs hover:bg-surface transition-colors" title="Link [text](url)">Link</button>
              <span className="text-[11px] text-muted-foreground ml-1">**îngroșat** · *cursiv* · [text](link)</span>
            </div>
            <textarea rows={7} value={form.content} onChange={setF("content")} data-testid="nl-content" className="inp resize-none" placeholder="Scrie mesajul... (rânduri noi permise)" />
          </F>
          <Row>
            <F label="Text buton (CTA)"><input value={form.cta_label} onChange={setF("cta_label")} data-testid="nl-cta-label" className="inp" placeholder="Ex: Comandă acum" /></F>
            <F label="Link buton"><input value={form.cta_url} onChange={setF("cta_url")} data-testid="nl-cta-url" className="inp" placeholder="https://coffeecup-supply.com/produse" /></F>
          </Row>
          <button type="submit" disabled={sending} data-testid="nl-send"
            className="bg-ink text-cream px-6 py-3 text-sm font-semibold hover:bg-roast transition-colors disabled:opacity-60">
            {sending ? "Se trimite..." : `Trimite către ${selected.length} abonați`}
          </button>
        </form>

        {/* Live preview */}
        <div className="mt-8">
          <p className="overline text-ink/50 mb-3">Previzualizare email</p>
          <div className="border border-line bg-[#F7F5F0] p-5" data-testid="nl-preview">
            <div className="mx-auto max-w-[420px] bg-white border border-line">
              <div className="bg-ink px-6 py-4"><span className="font-display text-xl text-cream">Coffee Cup <span className="text-sand">Supply</span></span></div>
              {form.image_url && <img src={form.image_url} alt="" className="w-full object-cover max-h-40" />}
              <div className="p-6">
                {form.subtitle && <p className="text-[11px] tracking-[2px] uppercase text-roast mb-1">{form.subtitle}</p>}
                <h3 className="font-display text-2xl">{form.subject || "Titlul newsletterului"}</h3>
                <p className="text-sm text-muted-foreground mt-3 whitespace-pre-line leading-relaxed"
                   dangerouslySetInnerHTML={{ __html: mdPreview(form.content) || "Conținutul mesajului va apărea aici..." }} />
                <span className="inline-block mt-5 bg-ink text-cream px-5 py-2.5 text-xs font-bold tracking-wide">{form.cta_label || "VEZI PRODUSELE"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <p className="overline text-ink/50 mb-3">Campanii trimise</p>
          <div className="border border-line bg-surface divide-y divide-line">
            {campaigns.length === 0 && <p className="p-4 text-sm text-muted-foreground">Nicio campanie trimisă încă.</p>}
            {campaigns.map((c) => (
              <div key={c.id} className="p-4 flex items-center justify-between gap-4" data-testid={`nl-campaign-${c.id}`}>
                <div><p className="font-medium text-sm">{c.subject}</p>
                  <p className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString("ro-RO")}</p></div>
                <span className="text-xs text-roast whitespace-nowrap">{c.sent}/{c.recipients} livrate</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="overline text-ink/50">Destinatari ({selected.length}/{subs.length})</p>
          <button onClick={() => setSelected(allSelected ? [] : subs.map((s) => s.email))} data-testid="nl-select-all"
            className="text-xs text-roast hover:underline">{allSelected ? "Deselectează tot" : "Selectează tot"}</button>
        </div>
        <div className="border border-line bg-surface divide-y divide-line max-h-[560px] overflow-y-auto" data-testid="nl-subscribers">
          {subs.length === 0 && <p className="p-4 text-sm text-muted-foreground">Niciun abonat încă.</p>}
          {subs.map((s, i) => (
            <label key={i} className="flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer hover:bg-stone/40" data-testid={`nl-sub-${i}`}>
              <input type="checkbox" checked={selected.includes(s.email)} onChange={() => toggleSub(s.email)} className="accent-roast h-4 w-4" data-testid={`nl-sub-check-${i}`} />
              <span className="truncate">{s.email}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

const Row = ({ children }) => <div className="grid grid-cols-2 gap-4">{children}</div>;
const F = ({ label, children }) => (
  <label className="block"><span className="overline text-ink/50">{label}</span><div className="mt-2">{children}</div></label>
);
const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm cursor-pointer">
    <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="accent-roast h-4 w-4" /> {label}
  </label>
);


const BRAND_EMPTY = { name: "", description: "", logo: "", order: 0, active: true };
function Brands() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BRAND_EMPTY);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/admin/brands").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(BRAND_EMPTY); setEditing("new"); };
  const openEdit = (b) => { setForm({ name: b.name, description: b.description || "", logo: b.logo || "", order: b.order || 0, active: b.active !== false }); setEditing(b.id); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Numele este obligatoriu"); return; }
    try {
      if (editing === "new") await api.post("/admin/brands", form);
      else await api.put(`/admin/brands/${editing}`, form);
      toast.success("Brand salvat"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (b) => {
    if (!window.confirm(`Ștergi brandul „${b.name}”?`)) return;
    try { await api.delete(`/admin/brands/${b.id}`); toast.message("Brand șters"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleActive = async (b) => { await api.put(`/admin/brands/${b.id}`, { name: b.name, description: b.description || "", logo: b.logo || "", order: b.order || 0, active: !(b.active !== false) }); load(); };
  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo: `${BACKEND}${data.url}` }));
    } catch (e) { toast.error("Upload eșuat"); } finally { setUploading(false); }
  };

  return (
    <div data-testid="admin-brands">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-2xl">Branduri</h2>
        <button onClick={openNew} data-testid="brand-new" className="inline-flex items-center gap-2 bg-ink text-cream px-4 py-2 text-sm font-semibold hover:bg-roast transition-colors"><Plus size={16} /> Adaugă brand</button>
      </div>
      <div className="border border-line bg-surface divide-y divide-line">
        {list.map((b) => (
          <div key={b.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`brand-row-${b.id}`}>
            <div className="h-12 w-24 bg-white border border-line flex items-center justify-center shrink-0">
              {b.logo ? <img src={b.logo} alt={b.name} className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-muted-foreground">fără logo</span>}
            </div>
            <div className="flex-1 min-w-[160px]">
              <p className="font-medium">{b.name} {b.active === false && <span className="text-xs text-muted-foreground">(inactiv)</span>}</p>
              <p className="text-xs text-muted-foreground truncate">{b.description || "—"} · {b.product_count} produse · ordine {b.order}</p>
            </div>
            <button onClick={() => toggleActive(b)} data-testid={`brand-toggle-${b.id}`} className="border border-line px-3 py-1.5 text-xs hover:border-ink">{b.active === false ? "Activează" : "Dezactivează"}</button>
            <button onClick={() => openEdit(b)} data-testid={`brand-edit-${b.id}`} className="p-2 hover:text-roast"><Pencil size={16} /></button>
            <button onClick={() => del(b)} data-testid={`brand-del-${b.id}`} className="p-2 hover:text-destructive"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-cream w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="brand-form">
            <div className="flex justify-between items-center mb-4"><h3 className="font-display text-xl">{editing === "new" ? "Brand nou" : "Editează brand"}</h3><button onClick={() => setEditing(null)}><X size={20} /></button></div>
            <div className="space-y-4">
              <F label="Nume"><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="brand-name" className="w-full border border-line px-3 py-2 text-sm bg-white" /></F>
              <F label="Descriere scurtă"><textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="brand-desc" className="w-full border border-line px-3 py-2 text-sm bg-white resize-none" /></F>
              <F label="Ordine afișare"><input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value) || 0 }))} data-testid="brand-order" className="w-full border border-line px-3 py-2 text-sm bg-white" /></F>
              <F label="Logo">
                <div className="flex items-center gap-3">
                  {form.logo && <img src={form.logo} alt="" className="h-12 w-24 object-contain bg-white border border-line" />}
                  <label className="inline-flex items-center gap-2 border border-line px-3 py-2 text-sm cursor-pointer hover:border-ink">
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Încarcă logo
                    <input type="file" accept="image/*" className="hidden" data-testid="brand-logo-upload" onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} />
                  </label>
                </div>
              </F>
              <Toggle label="Activ" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} testid="brand-active" />
              <button onClick={save} data-testid="brand-save" className="w-full bg-ink text-cream py-3 text-sm font-semibold hover:bg-roast transition-colors">Salvează</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FAQ_EMPTY = { question: "", answer: "", order: 0, active: true };
function Faq() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(FAQ_EMPTY);
  const load = () => api.get("/admin/faq").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const openNew = () => { setForm({ ...FAQ_EMPTY, order: list.length }); setEditing("new"); };
  const openEdit = (f) => { setForm({ question: f.question, answer: f.answer, order: f.order || 0, active: f.active !== false }); setEditing(f.id); };
  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) { toast.error("Completează întrebarea și răspunsul"); return; }
    try {
      if (editing === "new") await api.post("/admin/faq", form);
      else await api.put(`/admin/faq/${editing}`, form);
      toast.success("FAQ salvat"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (f) => { if (!window.confirm("Ștergi această intrare?")) return; await api.delete(`/admin/faq/${f.id}`); toast.message("Șters"); load(); };
  const toggleActive = async (f) => { await api.put(`/admin/faq/${f.id}`, { question: f.question, answer: f.answer, order: f.order || 0, active: !(f.active !== false) }); load(); };

  return (
    <div data-testid="admin-faq">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-2xl">FAQ</h2>
        <button onClick={openNew} data-testid="faq-new" className="inline-flex items-center gap-2 bg-ink text-cream px-4 py-2 text-sm font-semibold hover:bg-roast transition-colors"><Plus size={16} /> Adaugă întrebare</button>
      </div>
      <div className="border border-line bg-surface divide-y divide-line">
        {list.map((f) => (
          <div key={f.id} className="p-4 flex items-start gap-4 flex-wrap" data-testid={`faq-row-${f.id}`}>
            <div className="flex-1 min-w-[200px]">
              <p className="font-medium">{f.question} {f.active === false && <span className="text-xs text-muted-foreground">(inactiv)</span>}</p>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{f.answer}</p>
              <p className="text-[11px] text-ink/40 mt-1">ordine {f.order}</p>
            </div>
            <button onClick={() => toggleActive(f)} data-testid={`faq-toggle-${f.id}`} className="border border-line px-3 py-1.5 text-xs hover:border-ink">{f.active === false ? "Activează" : "Dezactivează"}</button>
            <button onClick={() => openEdit(f)} data-testid={`faq-edit-${f.id}`} className="p-2 hover:text-roast"><Pencil size={16} /></button>
            <button onClick={() => del(f)} data-testid={`faq-del-${f.id}`} className="p-2 hover:text-destructive"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-cream w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="faq-form">
            <div className="flex justify-between items-center mb-4"><h3 className="font-display text-xl">{editing === "new" ? "Întrebare nouă" : "Editează"}</h3><button onClick={() => setEditing(null)}><X size={20} /></button></div>
            <div className="space-y-4">
              <F label="Întrebare"><input value={form.question} onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))} data-testid="faq-question" className="w-full border border-line px-3 py-2 text-sm bg-white" /></F>
              <F label="Răspuns"><textarea rows={5} value={form.answer} onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))} data-testid="faq-answer" className="w-full border border-line px-3 py-2 text-sm bg-white resize-none" /></F>
              <F label="Ordine afișare"><input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value) || 0 }))} data-testid="faq-order" className="w-full border border-line px-3 py-2 text-sm bg-white" /></F>
              <Toggle label="Activ" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} testid="faq-active" />
              <button onClick={save} data-testid="faq-save" className="w-full bg-ink text-cream py-3 text-sm font-semibold hover:bg-roast transition-colors">Salvează</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FBT_EMPTY = { base_slug: "", product_slugs: [], order: 0, active: true };
function Fbt() {
  const [combos, setCombos] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(FBT_EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [suggest, setSuggest] = useState(null);
  const [loadingSuggest, setLoadingSuggest] = useState(false);

  const load = () => api.get("/admin/fbt").then(({ data }) => setCombos(data));
  useEffect(() => {
    load();
    api.get("/admin/products").then(({ data }) => setProducts(data));
  }, []);

  const openNew = () => { setForm({ ...FBT_EMPTY, order: combos.length }); setSearch(""); setEditing("new"); };
  const openEdit = (c) => { setForm({ base_slug: c.base_slug, product_slugs: [...(c.product_slugs || [])], order: c.order || 0, active: c.active !== false }); setSearch(""); setEditing(c.id); };

  const setBase = (slug) => setForm((f) => ({ ...f, base_slug: slug, product_slugs: f.product_slugs.filter((s) => s !== slug) }));
  const toggleProduct = (slug) => setForm((f) => {
    if (slug === f.base_slug) { toast.error("Produsul de bază nu poate fi asociat cu el însuși"); return f; }
    return f.product_slugs.includes(slug)
      ? { ...f, product_slugs: f.product_slugs.filter((s) => s !== slug) }
      : { ...f, product_slugs: [...f.product_slugs, slug] };
  });

  const save = async () => {
    if (!form.base_slug) { toast.error("Selectează produsul de bază"); return; }
    if (form.product_slugs.length < 1) { toast.error("Adaugă cel puțin un produs asociat"); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/fbt", form);
      else await api.put(`/admin/fbt/${editing}`, form);
      toast.success("Combinație salvată"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const del = async (c) => { if (!window.confirm("Ștergi această combinație?")) return; await api.delete(`/admin/fbt/${c.id}`); toast.message("Combinație ștearsă"); load(); };
  const toggleActive = async (c) => { await api.put(`/admin/fbt/${c.id}`, { base_slug: c.base_slug, product_slugs: c.product_slugs, order: c.order || 0, active: !(c.active !== false) }); load(); };

  const loadSuggestions = async () => {
    setLoadingSuggest(true);
    try { const { data } = await api.get("/admin/fbt/suggestions"); setSuggest(data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoadingSuggest(false); }
  };
  const applySuggestion = (s) => {
    setForm({ base_slug: s.base_slug, product_slugs: s.together.map((t) => t.slug), order: combos.length, active: true });
    setSearch(""); setEditing("new");
  };

  const filtered = products.filter((p) => `${p.brand} ${p.name}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="admin-fbt">
      <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
        <h2 className="font-display text-2xl">Cumpărate frecvent împreună</h2>
        <div className="flex items-center gap-2">
          <button onClick={loadSuggestions} data-testid="fbt-suggest-btn" className="inline-flex items-center gap-2 border border-line px-4 py-2 text-sm hover:border-ink transition-colors">
            {loadingSuggest ? <Loader2 size={16} className="animate-spin" /> : <TrendingUp size={16} />} Sugestii din comenzi
          </button>
          <button onClick={openNew} data-testid="fbt-new" className="inline-flex items-center gap-2 bg-ink text-cream px-4 py-2 text-sm font-semibold hover:bg-roast transition-colors"><Plus size={16} /> Adaugă combinație</button>
        </div>
      </div>

      {suggest && (
        <div className="border border-line bg-surface p-4 mb-6" data-testid="fbt-suggestions">
          <div className="flex justify-between items-center mb-3">
            <p className="font-medium text-sm">Sugestii bazate pe {suggest.order_count} comenzi valide (nepublicate automat)</p>
            <button onClick={() => setSuggest(null)} className="text-ink/50 hover:text-ink"><X size={16} /></button>
          </div>
          {suggest.suggestions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Date insuficiente în istoricul comenzilor pentru o sugestie relevantă.</p>
          ) : (
            <div className="space-y-3">
              {suggest.suggestions.map((s) => (
                <div key={s.base_slug} className="flex items-start gap-4 flex-wrap border-b border-line pb-3 last:border-0" data-testid={`fbt-suggestion-${s.base_slug}`}>
                  <div className="flex-1 min-w-[220px]">
                    <p className="text-sm font-medium">{s.base_label}</p>
                    <p className="text-xs text-muted-foreground mt-1">Comandat frecvent cu: {s.together.map((t) => `${t.label} (×${t.count})`).join(" · ")}</p>
                  </div>
                  <button onClick={() => applySuggestion(s)} data-testid={`fbt-use-suggestion-${s.base_slug}`} className="border border-line px-3 py-1.5 text-xs hover:border-roast hover:text-roast transition-colors">Folosește sugestia</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="border border-line bg-surface divide-y divide-line">
        {combos.length === 0 && <p className="p-4 text-sm text-muted-foreground">Nicio combinație configurată încă.</p>}
        {combos.map((c) => (
          <div key={c.id} className="p-4 flex items-start gap-4 flex-wrap" data-testid={`fbt-row-${c.id}`}>
            <div className="flex-1 min-w-[220px]">
              <p className="font-medium">{c.base?.label} {c.active === false && <span className="text-xs text-muted-foreground">(inactiv)</span>} {c.base?.missing && <span className="text-xs text-destructive">(produs lipsă)</span>}</p>
              <p className="text-xs text-muted-foreground mt-1">
                → {c.products?.map((p) => p.label).join(" + ")}
                {c.products?.some((p) => p.missing) && <span className="text-destructive"> · conține produse lipsă</span>}
              </p>
              <p className="text-[11px] text-ink/40 mt-1">ordine {c.order}</p>
            </div>
            <button onClick={() => toggleActive(c)} data-testid={`fbt-toggle-${c.id}`} className="border border-line px-3 py-1.5 text-xs hover:border-ink">{c.active === false ? "Activează" : "Dezactivează"}</button>
            <button onClick={() => openEdit(c)} data-testid={`fbt-edit-${c.id}`} className="p-2 hover:text-roast"><Pencil size={16} /></button>
            <button onClick={() => del(c)} data-testid={`fbt-del-${c.id}`} className="p-2 hover:text-destructive"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-cream w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="fbt-form">
            <div className="flex justify-between items-center mb-4"><h3 className="font-display text-xl">{editing === "new" ? "Combinație nouă" : "Editează combinația"}</h3><button onClick={() => setEditing(null)}><X size={20} /></button></div>
            <div className="space-y-4">
              <F label="Produs de bază">
                <select value={form.base_slug} onChange={(e) => setBase(e.target.value)} data-testid="fbt-base-select" className="w-full border border-line px-3 py-2 text-sm bg-white">
                  <option value="">— Alege produsul —</option>
                  {products.map((p) => <option key={p.slug} value={p.slug}>{p.brand} {p.name}</option>)}
                </select>
              </F>
              <F label={`Produse asociate (${form.product_slugs.length} selectate)`}>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută produs..." data-testid="fbt-product-search" className="w-full border border-line px-3 py-2 text-sm bg-white mb-2" />
                <div className="border border-line bg-white divide-y divide-line max-h-64 overflow-y-auto">
                  {filtered.filter((p) => p.slug !== form.base_slug).map((p) => (
                    <label key={p.slug} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-cream/50" data-testid={`fbt-option-${p.slug}`}>
                      <input type="checkbox" checked={form.product_slugs.includes(p.slug)} onChange={() => toggleProduct(p.slug)} className="accent-roast h-4 w-4" />
                      <span className="text-sm">{p.brand} {p.name}</span>
                    </label>
                  ))}
                </div>
              </F>
              <F label="Ordine afișare"><input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value) || 0 }))} data-testid="fbt-order" className="w-full border border-line px-3 py-2 text-sm bg-white" /></F>
              <Toggle label="Activ" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} testid="fbt-active" />
              <button onClick={save} disabled={saving} data-testid="fbt-save" className="w-full bg-ink text-cream py-3 text-sm font-semibold hover:bg-roast transition-colors disabled:opacity-60">{saving ? "Se salvează..." : "Salvează combinația"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Settings() {
  const [social, setSocial] = useState({ instagram: "", facebook: "", tiktok: "" });
  const [products, setProducts] = useState([]);
  const [recommended, setRecommended] = useState([]);
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => { setSocial({ instagram: data.instagram || "", facebook: data.facebook || "", tiktok: data.tiktok || "" }); setRecommended(data.recommended || []); });
    api.get("/products").then(({ data }) => setProducts(data));
  }, []);
  const saveSocial = async () => { await api.put("/admin/settings", social); toast.success("Linkuri salvate"); };
  const toggleRec = (slug) => setRecommended((r) => r.includes(slug) ? r.filter((s) => s !== slug) : (r.length >= 4 ? (toast.error("Maxim 4 produse"), r) : [...r, slug]));
  const saveRec = async () => {
    if (recommended.length !== 0 && (recommended.length < 2 || recommended.length > 4)) { toast.error("Selectează între 2 și 4 produse"); return; }
    try { await api.put("/admin/settings", { recommended }); toast.success("Recomandările casei salvate"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-settings" className="space-y-10 max-w-2xl">
      <div>
        <h2 className="font-display text-2xl mb-4">Rețele sociale</h2>
        <div className="space-y-4">
          {[["instagram", "Instagram"], ["facebook", "Facebook"], ["tiktok", "TikTok"]].map(([k, l]) => (
            <F key={k} label={l}><input value={social[k]} onChange={(e) => setSocial((s) => ({ ...s, [k]: e.target.value }))} data-testid={`settings-${k}`} className="w-full border border-line px-3 py-2 text-sm bg-white" placeholder="https://..." /></F>
          ))}
          <button onClick={saveSocial} data-testid="settings-social-save" className="bg-ink text-cream px-5 py-2.5 text-sm font-semibold hover:bg-roast transition-colors">Salvează linkurile</button>
        </div>
      </div>
      <div>
        <h2 className="font-display text-2xl mb-1">Recomandările casei</h2>
        <p className="text-sm text-muted-foreground mb-4">Selectează între 2 și 4 produse ({recommended.length} selectate). Gol = produse populare automat.</p>
        <div className="border border-line bg-surface divide-y divide-line max-h-96 overflow-y-auto">
          {products.map((p) => (
            <label key={p.slug} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-cream/50" data-testid={`rec-option-${p.slug}`}>
              <input type="checkbox" checked={recommended.includes(p.slug)} onChange={() => toggleRec(p.slug)} className="accent-roast h-4 w-4" />
              <span className="text-sm">{p.brand} {p.name}</span>
            </label>
          ))}
        </div>
        <button onClick={saveRec} data-testid="settings-rec-save" className="mt-4 bg-ink text-cream px-5 py-2.5 text-sm font-semibold hover:bg-roast transition-colors">Salvează recomandările</button>
      </div>
    </div>
  );
}
